/**
 * @file Case Study Audio Player
 */

const vAudio = () => {
	const audioPlayers = document.querySelectorAll('.a-cs-audio__player');
	if (!audioPlayers.length) {
		return;
	}

	class AudioPlayer {
		constructor(player) {
			this.player = player;

			// Controls
			this.playPause = player.querySelector('.a-cs-audio__play-pause');
			this.back = player.querySelector('.a-cs-audio__skip--back');
			this.forward = player.querySelector('.a-cs-audio__skip--forward');
			this.progressTrack = player.querySelector('.a-cs-audio__progress');
			this.progressBar = player.querySelector('.a-cs-audio__progress-bar');
			this.progressNode = player.querySelector('.a-cs-audio__progress-node');

			// Elements
			this.trackTitle = player.querySelector('.a-cs-audio__track-title');
			this.durationTitle = player.querySelector('.a-cs-audio__duration');
			this.currentTimeTitle = player.querySelector('.a-cs-audio__time--current');
			this.totalTimeTitle = player.querySelector('.a-cs-audio__time--total');

			// Tracks
			this.tracks = player.querySelectorAll('audio');

			// Current Track Info
			this.currentTrackIndex = 0;
			this.currentTrack = this.tracks[this.currentTrackIndex];
			this.currentDuration = null;

			// Loading
			this.isLoading;

			// Progress track layout
			this.trackWidth;
			this.trackOffsetLeft;

			// Scrub
			this.scrubbing = false;
			this.scrubWasPlaying = false;

			this.init();
		}

		init() {
			// Hide skip buttons
			if (this.tracks.length < 2) {
				this.back.classList.add('a-cs-audio__skip--hidden');
				this.forward.classList.add('a-cs-audio__skip--hidden');
			}

			// Hook up skip buttons
			this.back.addEventListener('click', this.skipBack.bind(this));
			this.forward.addEventListener('click', this.skipForward.bind(this));

			// Hook up play button
			this.playPause.addEventListener('click', this.togglePlay.bind(this));

			// Hook up scrub node
			this.progressNode.addEventListener('mousedown', this.grab.bind(this));
			this.progressNode.addEventListener('touchstart', this.grab.bind(this));
			window.addEventListener('mouseup', this.release.bind(this));
			window.addEventListener('touchend', this.release.bind(this));
			window.addEventListener('mousemove', this.scrub.bind(this));
			window.addEventListener('touchmove', this.scrubTouch.bind(this));

			// Update width
			this.updateTrackDimensions();
			window.addEventListener('resize', this.updateTrackDimensions.bind(this));

			// Set current track
			this.awaitCurrentTrack();
		}

		updateTrackDimensions() {
			const layout = this.progressTrack.getBoundingClientRect();
			this.trackWidth = layout.width;
			this.trackOffsetLeft = layout.left;

			if (this.currentTrack) {
				this.animateProgress(false);
			}
		}

		awaitCurrentTrack(isPlaying) {
			clearInterval(this.isLoading);

			const testReadyState = () => {
				if (this.currentTrack.readyState >= 1) {
					this.loadCurrentTrack(isPlaying);
					clearInterval(this.isLoading);
				}
			};
			this.isLoading = setInterval(testReadyState, 200);
		}

		loadCurrentTrack(play) {
			// Set duration info
			this.currentDuration = Math.floor(this.currentTrack.duration);
			const durationMin = Math.floor(this.currentDuration / 60);
			const durationSec = this.currentDuration % 60;
			const minTitle = durationMin ? `${durationMin}min ` : '';
			const secTitle = `${durationSec}sec`;

			this.durationTitle.innerHTML = minTitle + secTitle;
			this.totalTimeTitle.innerHTML = `${durationMin}:${('' + durationSec).padStart(2, '0')}`;

			// Set current time
			this.currentTrack.currentTime = 0;
			this.animateProgress(false);

			// Set track title
			this.trackTitle.innerHTML = this.currentTrack.dataset.title;

			// Disable skip buttons
			if (this.currentTrackIndex === 0) {
				this.back.classList.add('a-cs-audio__skip--disabled');
			} else {
				this.back.classList.remove('a-cs-audio__skip--disabled');
			}

			if (this.currentTrackIndex === this.tracks.length - 1) {
				this.forward.classList.add('a-cs-audio__skip--disabled');
			} else {
				this.forward.classList.remove('a-cs-audio__skip--disabled');
			}

			// Track progress and play track
			this.currentTrack.addEventListener('timeupdate', this.progress.bind(this));
			if (play) {
				this.playTrack();
				this.animateProgress();
			}
		}

		unloadCurrentTrack() {
			// Stop tracking progress and pause track
			this.currentTrack.removeEventListener('timeupdate', this.progress);
			this.pauseTrack();
		}

		pauseTrack() {
			this.currentTrack.pause();
			this.playPause.classList.remove('a-cs-audio__play-pause--playing');
		}

		playTrack() {
			this.currentTrack.play();
			this.playPause.classList.add('a-cs-audio__play-pause--playing');
			this.animateProgress();
		}

		togglePlay() {
			// Play or pause, toggle class accordingly
			if (this.currentTrack.paused) {
				this.playTrack();
			} else {
				this.pauseTrack();
			}
		}

		skipBack() {
			if (this.currentTrackIndex === 0) return;

			// Stop current track, load previous
			let isPlaying = !this.currentTrack.paused;
			this.unloadCurrentTrack();
			this.currentTrackIndex -= 1;
			this.currentTrack = this.tracks[this.currentTrackIndex];
			this.awaitCurrentTrack(isPlaying);
		}

		skipForward() {
			if (this.currentTrackIndex === this.tracks.length - 1) return;

			// Stop current track, load next
			let isPlaying = !this.currentTrack.paused;
			this.unloadCurrentTrack();
			this.currentTrackIndex += 1;
			this.currentTrack = this.tracks[this.currentTrackIndex];
			this.awaitCurrentTrack(isPlaying);
		}

		progress() {
			const currentTime = Math.floor(this.currentTrack.currentTime);
			const currentMin = Math.floor(currentTime / 60);
			const currentSec = currentTime % 60;

			// Set current time title
			this.currentTimeTitle.innerHTML = `${currentMin}:${('' + currentSec).padStart(2, '0')}`;
		}

		animateProgress(loop = true) {
			if (this.currentTrack.paused && loop) return;

			// Calculate and set progress node
			const progress = this.currentTrack.currentTime / this.currentDuration;
			this.progressNode.style.transform = `translateX(-50%) translateX(${this.trackWidth * progress}px)`;
			this.progressBar.style.transform = `translateY(-50%) scaleX(${progress})`;

			if (loop) {
				requestAnimationFrame(this.animateProgress.bind(this));
			}
		}

		grab(e) {
			e.preventDefault();

			// Pause the track and start scrub
			this.scrubbing = true;
			this.scrubWasPlaying = !this.currentTrack.paused;
			this.pauseTrack();
		}

		release() {
			if (!this.scrubbing) return;

			// Stop scrub, resume play if it was playing before scrub
			this.scrubbing = false;
			if (this.scrubWasPlaying) {
				this.playTrack();
			}
		}

		scrub(e) {
			if (!this.scrubbing) return;

			// Find mouse position relative to the track
			const fromLeft = Math.max((e.clientX - this.trackOffsetLeft), 0);
			const progress = Math.min((fromLeft / this.trackWidth), 1);

			// Calculate and set the track time, animate progress
			const newTime = this.currentDuration * progress;
			this.currentTrack.currentTime = newTime;
			this.animateProgress(false);
		}

		scrubTouch(e) {
			if (!this.scrubbing) return;

			// Find mouse position relative to the track
			const fromLeft = Math.max((e.touches[0].clientX - this.trackOffsetLeft), 0);
			const progress = Math.min((fromLeft / this.trackWidth), 1);

			// Calculate and set the track time, animate progress
			const newTime = this.currentDuration * progress;
			this.currentTrack.currentTime = newTime;
			this.animateProgress(false);
		}
	}

	const players = [...audioPlayers].map(player => new AudioPlayer(player));

	// window.onOverlayStateChange.push(function(){
	// 	if (window.overlayIsOpen && players && players.length) {
	// 		players.forEach(player => player.pauseTrack());
	// 	}
	// });
	document.addEventListener('vOverlayTransitioning', function() {
		// const {state} = data;

		// if (state === 'open' && players && players.length) {
		if (players && players.length) { // vOverlayTransitioning event is fired only on overlay close
			players.forEach(player => player.pauseTrack());
		}
	});
};

export default vAudio;
